/**
 * SectionExplainer — Priority #8 surface.
 *
 * Inline, not a slide-over: the output is short (3-4 sentences) and
 * the user wants to read it WHILE looking at the section text. Slide-
 * over would force them to scroll/eye-flick. So we drop the result
 * into a small card directly below the section's controls.
 *
 * Caching: the backend stores explanations on
 * ``rfp.analysis['explanations'][section_index]``. The first click
 * pays the LLM round-trip; subsequent clicks (until the next
 * /analyze) return ``cached=true`` and are instantaneous. The
 * "Refresh" button forces a new call when the analyst wants to
 * regenerate.
 *
 * Three distinct failure surfaces (matching the backend's status codes):
 *   503 — LLM not configured. Show a clear "configure LLM_API_KEY" hint.
 *   502 — Upstream Gemini failure. Suggest retry.
 *   400/404 — programmer error; toast and bail.
 */
import React, { useCallback, useState } from "react";
import {
  Sparkles, RefreshCcw, Loader2, AlertTriangle, Settings,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { v2Api } from "@/lib/v2Api";


export default function SectionExplainer({ rfpId, sectionIndex }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(false);
  // ``error`` carries {kind: 'missing-key'|'upstream'|'other', detail: string}
  // so the empty-state can render the right CTA rather than a generic toast.
  const [error, setError] = useState(null);

  const run = useCallback(async ({ force = false } = {}) => {
    if (sectionIndex == null || rfpId == null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await v2Api.rfp.explainSection(rfpId, sectionIndex, { force });
      setText(data.explanation || "");
      setCached(!!data.cached);
      setOpen(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail ?? err?.message ?? "Explain failed";
      let kind = "other";
      if (status === 503) kind = "missing-key";
      else if (status === 502) kind = "upstream";
      setError({ kind, detail });
      setText("");
      setOpen(true);
      // For 400 / 404 it's almost always programmer error; surface the
      // toast too so it shows up in dev tools.
      if (status === 400 || status === 404) toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, [rfpId, sectionIndex]);

  const noIndex = sectionIndex == null;

  if (!open) {
    return (
      <Button
        variant="outline" size="sm"
        disabled={noIndex || loading}
        onClick={() => run({ force: false })}
        className="h-7 text-xs gap-1"
        title="Plain-English summary via LLM"
      >
        {loading
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <Sparkles className="w-3 h-3" />}
        Explain in plain English
      </Button>
    );
  }

  return (
    <div className="mt-2 border rounded-md bg-blue-50/40 border-blue-200 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-500" />
          <span className="text-xs font-semibold text-slate-800">
            Plain-English summary
          </span>
          {cached && !error && (
            <Badge variant="outline" className="text-[10px] text-slate-500">
              cached
            </Badge>
          )}
        </div>
        <div className="flex gap-1">
          <Button
            size="sm" variant="ghost"
            disabled={loading}
            onClick={() => run({ force: true })}
            className="h-6 text-[11px] gap-1"
            title="Regenerate (uses an LLM round-trip)"
          >
            {loading
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <RefreshCcw className="w-3 h-3" />}
            Refresh
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => setOpen(false)}
            className="h-6 text-[11px]"
          >
            Hide
          </Button>
        </div>
      </div>

      {loading && !text && (
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Summarising…
        </p>
      )}

      {error?.kind === "missing-key" && (
        <div className="text-xs text-slate-700 space-y-1">
          <p className="flex items-center gap-1.5 text-amber-700">
            <Settings className="w-3.5 h-3.5" />
            LLM isn't configured on this workspace.
          </p>
          <p className="text-slate-500">
            An admin needs to set <code className="font-mono text-[11px]">LLM_API_KEY</code>
            {" "}in <code className="font-mono text-[11px]">backend/.env</code> and restart
            the backend. Without it, plain-English summarisation,
            executive summaries, and LLM-extracted fields all stay empty.
          </p>
        </div>
      )}

      {error?.kind === "upstream" && (
        <div className="text-xs text-slate-700 space-y-1">
          <p className="flex items-center gap-1.5 text-rose-700">
            <AlertTriangle className="w-3.5 h-3.5" />
            The LLM call failed.
          </p>
          <p className="text-slate-500">{error.detail}</p>
          <Button size="sm" variant="outline" onClick={() => run({ force: true })}
                  className="h-6 text-[11px] mt-1">
            Try again
          </Button>
        </div>
      )}

      {error?.kind === "other" && (
        <p className="text-xs text-rose-700 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          {error.detail}
        </p>
      )}

      {!error && text && (
        <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
          {text}
        </p>
      )}
    </div>
  );
}
